import { useEffect, useState } from "react";
import {
  Drawer,
  Spin,
  Typography,
  Button,
  Divider,
  List,
  Descriptions,
  Empty,
  Avatar,
  Tag,
  Space,
  Timeline,
  Form,
  Card,
  Input,
  Upload,
  Col,
  Row,
  message,
  Select,
  Collapse,
  Alert,
} from "antd";

import {
  DownloadOutlined,
  UserOutlined,
  CustomerServiceOutlined,
  InboxOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileTextOutlined,
  SendOutlined,
  UploadOutlined,
  ConsoleSqlOutlined,
  DownOutlined,
  TagsOutlined,
  DeploymentUnitOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

import {
  addComment,
  downloadCommentAttachment,
  downloadTicketAttachment,
  getTicketDetails,
} from "./ticketApi";

import { Ticket } from "lucide-react";

const { Title, Paragraph, Text } = Typography;

interface Attachment {
  attachmentId: number;
  fileName: string;
  fileSize: string;
}

interface TicketComment {
  id: number;
  user: string;
  role: "CLIENT" | "SUPPORT";
  comment: string;
  status: string;
  createdOn: string;
  attachments: Attachment[];
}

interface TicketDetails {
  ticketId: string | number | null;
  title: string;
  category: string;
  ticketType: string;
  environment: string;
  status: string;
  createdOn: string;
  updatedOn: string;
  errorCode?: string;
  description: string;
  attachments: Attachment[];
  comments: TicketComment[];
}

interface ViewTicketDrawerProps {
  open: boolean;
  ticketId: string | null | number;
  onClose: () => void;
}

const ViewTicketDrawer = ({
  open,
  ticketId,
  onClose,
}: ViewTicketDrawerProps) => {
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState<TicketDetails | null>(null);
  const [replyFiles, setReplyFiles] = useState<any[]>([]);
  const [replyForm] = Form.useForm();

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "pdf":
        return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;

      case "png":
      case "jpg":
      case "jpeg":
        return <FileImageOutlined style={{ color: "#1677ff" }} />;

      default:
        return <FileTextOutlined />;
    }
  };

  const loadTicketDetails = async () => {
    if (!ticketId) return;

    setLoading(true);

    try {
      const response = await getTicketDetails(ticketId);

      console.log("ticketdetails", response);

      setTicket(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !ticketId) return;

    loadTicketDetails();
  }, [open, ticketId]);

  const handleReplySubmit = async (values: any) => {
    console.log(values);
    console.log(replyFiles);

    try {
      await addComment(ticketId, values, replyFiles);

      message.success("Comment added successfully");

      replyForm.resetFields();

      setReplyFiles([]);

      await loadTicketDetails();
    } catch (e) {
      console.error(e);

      message.error("Unable to add comment");
    }
  };

  const downloadFile = async (attachment: Attachment) => {
    console.log("Downloading:", attachment.fileName);

    try {
      const response = await downloadTicketAttachment(
        attachment.attachmentId
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");

      link.href = url;
      link.download = attachment.fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Unable to download file");
    }
  };

  const downloadcommentFile = async (attachment: Attachment) => {
    console.log("Downloading:", attachment.fileName);

    try {
      const response = await downloadCommentAttachment(
        attachment.attachmentId
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");

      link.href = url;
      link.download = attachment.fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch {
      message.error("Unable to download file");
    }
  };

  const getStatusOptions = (status: string) => {
    switch (status) {
      case "OPEN":
        return [
          { label: "Open", value: "OPEN" },
          { label: "Closed", value: "CLOSED" },
        ];

      case "RESOLVED":
        return [
          { label: "Resolved", value: "RESOLVED" },
          { label: "Open", value: "OPEN" },
          { label: "Closed", value: "CLOSED" },
        ];

      case "CLOSED":
        return [{ label: "Closed", value: "CLOSED" }];

      case "ASSIGNED":
        return [{ label: "Assigned", value: "ASSIGNED" }];

      case "IN_PROGRESS":
        return [{ label: "Closed", value: "CLOSED" }];

      case "ON_HOLD":
        return [{ label: "On Hold", value: "ON_HOLD" }];

      default:
        return [
          {
            label: status.replace("_", " "),
            value: status,
          },
        ];
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "NEW":
        return "blue";

      case "ASSIGNED":
        return "cyan";

      case "IN_PROGRESS":
        return "orange";

      case "ON_HOLD":
        return "gold";

      case "RESOLVED":
        return "green";

      case "CLOSED":
        return "default";

      default:
        return "default";
    }
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  /*
   * Allowed characters in comment:
   *
   * A-Z
   * a-z
   * 0-9
   * spaces
   * .
   * ,
   * !
   * ?
   * :
   * ;
   * (
   * )
   * -
   * _
   */
  const sanitizeComment = (value: string): string => {
    return value.replace(/[^A-Za-z0-9\s.,!?:;()_-]/g, "");
  };

  return (
    <Drawer
      title={`Ticket Details - ${ticketId}`}
      open={open}
      onClose={onClose}
      size={1000}
      destroyOnHidden
    >
      {loading ? (
        <Spin size="large" />
      ) : (
        <>
          {ticket && (
            <>
              {/* Header */}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div>
                  <Title level={4} style={{ marginBottom: 4 }}>
                    {ticket.ticketId}
                  </Title>

                  <Text type="secondary">
                    Created on{" "}
                    {new Date(ticket.createdOn).toLocaleDateString(
                      "en-GB"
                    )}
                  </Text>
                </div>

                <Tag
                  color={getStatusColor(ticket.status)}
                  style={{
                    padding: "6px 14px",
                    fontSize: 14,
                    borderRadius: 20,
                  }}
                >
                  {ticket.status.replaceAll("_", " ")}
                </Tag>
              </div>

              {/* Ticket Information */}

              <Title level={5}>Ticket Information</Title>

              <Descriptions
                bordered
                size="middle"
                column={2}
                style={{ marginBottom: 24 }}
              >
                <Descriptions.Item label="Title">
                  <Space>
                    <Avatar
                      size="small"
                      icon={<FileTextOutlined />}
                      style={{
                        background: "#1677ff",
                      }}
                    />

                    {ticket.title}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Category">
                  <Space>
                    <Avatar
                      size="small"
                      icon={<TagsOutlined />}
                      style={{
                        background: "#13c2c2",
                      }}
                    />

                    {ticket.category}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Environment">
                  <Space>
                    <Avatar
                      size="small"
                      icon={<DeploymentUnitOutlined />}
                      style={{
                        background: "#722ed1",
                      }}
                    />

                    {ticket.environment || "-"}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(ticket.status)}>
                    {ticket.status.replaceAll("_", " ")}
                  </Tag>
                </Descriptions.Item>

                {ticket.errorCode && (
                  <Descriptions.Item label="Error Code" span={2}>
                    {ticket.errorCode}
                  </Descriptions.Item>
                )}

                <Descriptions.Item label="Created On">
                  <Space size={10}>
                    <Avatar
                      size={32}
                      icon={<CalendarOutlined />}
                      style={{ background: "#eb2f96" }}
                    />

                    <Typography.Text>
                      {new Date(
                        ticket.createdOn
                      ).toLocaleDateString("en-GB")}
                    </Typography.Text>
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item label="Updated On">
                  <Space size={10}>
                    <Avatar
                      size={32}
                      icon={<CalendarOutlined />}
                      style={{ background: "#fa541c" }}
                    />

                    <Typography.Text>
                      {new Date(
                        ticket.updatedOn
                      ).toLocaleDateString("en-GB")}
                    </Typography.Text>
                  </Space>
                </Descriptions.Item>
              </Descriptions>

              <Title level={5}>Issue Description</Title>

              <Paragraph
                style={{
                  marginBottom: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {ticket.description}
              </Paragraph>

              {ticket.attachments?.length > 0 && (
                <>
                  <Text strong>Attachments:</Text>

                  <div style={{ marginTop: 8 }}>
                    {ticket.attachments.map((file) => (
                      <div key={file.attachmentId}>
                        <Button
                          type="link"
                          size="small"
                          icon={getFileIcon(file.fileName)}
                          style={{ padding: 0 }}
                          onClick={() => downloadFile(file)}
                        >
                          {file.fileName}
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <Divider />

              <Title level={5}>Conversation</Title>

              {ticket.comments.length === 0 ? (
                <Empty
                  description="No comments available"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <List
                  itemLayout="vertical"
                  dataSource={ticket.comments}
                  split={false}
                  renderItem={(comment) => (
                    <List.Item
                      style={{
                        padding: 0,
                        marginBottom: 22,
                        border: "none",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 16,
                        }}
                      >
                        <Avatar
                          size={48}
                          style={{
                            backgroundColor:
                              comment.role === "SUPPORT"
                                ? "#1677ff"
                                : "#52c41a",
                            fontSize: 18,
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(comment.user)}
                        </Avatar>

                        <Card
                          size="small"
                          style={{
                            width: "100%",
                            borderRadius: 12,
                            boxShadow:
                              "0 2px 8px rgba(0,0,0,0.06)",
                          }}
                        >
                          <Row
                            justify="space-between"
                            align="top"
                          >
                            <Col>
                              <Space
                                direction="vertical"
                                size={4}
                              >
                                <Text
                                  strong
                                  style={{ fontSize: 15 }}
                                >
                                  {comment.user}
                                </Text>

                                <Space size={8}>
                                  <Tag
                                    color={
                                      comment.role === "SUPPORT"
                                        ? "blue"
                                        : "green"
                                    }
                                  >
                                    {comment.role}
                                  </Tag>

                                  <Tag color="processing">
                                    {comment.status}
                                  </Tag>
                                </Space>
                              </Space>
                            </Col>

                            <Col>
                              <Text type="secondary">
                                {formatDateTime(
                                  comment.createdOn
                                )}
                              </Text>
                            </Col>
                          </Row>

                          <Paragraph
                            style={{
                              marginTop: 18,
                              marginBottom: 0,
                              lineHeight: 1.8,
                              fontSize: 15,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {comment.comment}
                          </Paragraph>

                          {comment.attachments?.length > 0 && (
                            <>
                              <Divider
                                style={{
                                  marginTop: 18,
                                  marginBottom: 14,
                                }}
                              />

                              <Text
                                strong
                                style={{
                                  display: "block",
                                  marginBottom: 12,
                                }}
                              >
                                Attachments
                              </Text>

                              <Space
                                direction="vertical"
                                style={{ width: "100%" }}
                                size={10}
                              >
                                {comment.attachments.map(
                                  (attachment) => (
                                    <Card
                                      key={
                                        attachment.attachmentId
                                      }
                                      hoverable
                                      size="small"
                                      bodyStyle={{
                                        padding: "10px 14px",
                                      }}
                                      style={{
                                        borderRadius: 8,
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        downloadcommentFile(
                                          attachment
                                        )
                                      }
                                    >
                                      <Row
                                        justify="space-between"
                                        align="middle"
                                      >
                                        <Space>
                                          <Avatar
                                            style={{
                                              background:
                                                "#e6f4ff",
                                              color: "#1677ff",
                                            }}
                                          >
                                            {getFileIcon(
                                              attachment.fileName
                                            )}
                                          </Avatar>

                                          <div>
                                            <Text strong>
                                              {
                                                attachment.fileName
                                              }
                                            </Text>

                                            <br />

                                            <Text
                                              type="secondary"
                                              style={{
                                                fontSize: 12,
                                              }}
                                            >
                                              {
                                                attachment.fileSize
                                              }
                                            </Text>
                                          </div>
                                        </Space>

                                        <DownloadOutlined
                                          style={{
                                            color: "#1677ff",
                                            fontSize: 18,
                                          }}
                                        />
                                      </Row>
                                    </Card>
                                  )
                                )}
                              </Space>
                            </>
                          )}
                        </Card>
                      </div>
                    </List.Item>
                  )}
                />
              )}

              <Collapse
                ghost
                expandIcon={({ isActive }) => (
                  <DownOutlined
                    rotate={isActive ? 180 : 0}
                    style={{
                      fontSize: 16,
                      color: "#1677ff",
                      transition: "0.3s",
                    }}
                  />
                )}
                style={{
                  marginTop: 24,
                  border: "1px solid #e6e6e6",
                  borderRadius: 12,
                  background: "#ffffff",
                  overflow: "hidden",
                  boxShadow:
                    "0 2px 8px rgba(0,0,0,0.05)",
                }}
                items={[
                  {
                    key: "1",

                    label: (
                      <Row
                        justify="space-between"
                        align="middle"
                      >
                        <Text
                          strong
                          style={{
                            fontSize: 16,
                            color: "#1677ff",
                          }}
                        >
                          Add Comment
                        </Text>

                        <Tag color="processing">
                          {ticket.status.replace("_", " ")}
                        </Tag>
                      </Row>
                    ),

                    children: (
                      <Form
                        form={replyForm}
                        layout="vertical"
                        onFinish={handleReplySubmit}
                      >
                        <Alert
                          showIcon
                          type="info"
                          style={{ marginBottom: 20 }}
                          message={
                            ["OPEN", "IN_PROGRESS"].includes(
                              ticket.status
                            )
                              ? "You may close the ticket if your issue has been resolved."
                              : ticket.status === "RESOLVED"
                              ? "You may reopen the ticket if the issue persists or close it if it has been resolved."
                              : "Add your comment or supporting attachments. Ticket status cannot be changed at this stage."
                          }
                        />

                        {/* Status */}

                        <Form.Item
                          label="Ticket Status"
                          name="status"
                          initialValue={ticket.status}
                          rules={[
                            {
                              required: true,
                              message:
                                "Please select ticket status.",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select Status"
                            options={getStatusOptions(
                              ticket.status
                            )}
                            disabled={
                              ![
                                "OPEN",
                                "RESOLVED",
                                "IN_PROGRESS",
                              ].includes(ticket.status)
                            }
                          />
                        </Form.Item>

                        {/* Comment */}

                        <Form.Item
                          label="Comment"
                          name="comment"
                          rules={[
                            {
                              required: true,
                              message:
                                "Please enter your comment.",
                            },
                            {
                              min: 10,
                              message:
                                "Minimum 10 characters required.",
                            },
                            {
                              max: 1000,
                              message:
                                "Maximum 1000 characters allowed.",
                            },
                           {
            validator: (_, value) => {
                if (!value) {
                    return Promise.resolve();
                }

                const allowedPattern =
                    /^[A-Za-z0-9\s.,!?:;()_-]+$/;

                if (!allowedPattern.test(value)) {
                    return Promise.reject(
                        new Error(
                            "Special characters are not allowed. Allowed: . , ! ? : ; ( ) - _"
                        )
                    );
                }

                return Promise.resolve();
            },
        },
                          ]}
                        >
                          <Input.TextArea
                            rows={5}
                            placeholder="Describe your issue or provide additional information..."
                            showCount
                            maxLength={1000}
                           
                          />
                        </Form.Item>

                        {/* Attachments */}

                        <Form.Item
                          label="Attachments"
                          extra="Maximum 5 files • 5 MB each. Allowed: PDF, PNG, JPG, JPEG, DOC, DOCX, XLS, XLSX, ZIP, TXT"
                        >
                          <Upload
                            multiple
                            maxCount={5}
                            fileList={replyFiles}
                            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.zip,.txt"
                            beforeUpload={(file) => {
                              const allowedExtensions = [
                                "pdf",
                                "png",
                                "jpg",
                                "jpeg",
                                "doc",
                                "docx",
                                "xls",
                                "xlsx",
                                "zip",
                                "txt",
                              ];

                              const allowedMimeTypes = [
                                "application/pdf",
                                "image/png",
                                "image/jpeg",
                                "application/msword",
                                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                "application/vnd.ms-excel",
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                                "application/zip",
                                "text/plain",
                              ];

                              const fileName = file.name || "";

                              const lastDot =
                                fileName.lastIndexOf(".");

                              if (lastDot === -1) {
                                message.error(
                                  `${fileName}: File extension is required.`
                                );

                                return Upload.LIST_IGNORE;
                              }

                              const extension = fileName
                                .substring(lastDot + 1)
                                .toLowerCase();

                              if (
                                !allowedExtensions.includes(
                                  extension
                                )
                              ) {
                                message.error(
                                  `${fileName}: This file type is not allowed.`
                                );

                                return Upload.LIST_IGNORE;
                              }

                              if (
                                file.type &&
                                !allowedMimeTypes.includes(
                                  file.type
                                )
                              ) {
                                message.error(
                                  `${fileName}: Invalid file content type.`
                                );

                                return Upload.LIST_IGNORE;
                              }

                              const isWithinSizeLimit =
                                file.size <=
                                5 * 1024 * 1024;

                              if (!isWithinSizeLimit) {
                                message.error(
                                  `${fileName}: File must be smaller than 5 MB.`
                                );

                                return Upload.LIST_IGNORE;
                              }

                              const dangerousFilename =
                                /[<>:"/\\|?*\x00-\x1F]/;

                              if (
                                dangerousFilename.test(
                                  fileName
                                )
                              ) {
                                message.error(
                                  `${fileName}: Invalid file name.`
                                );

                                return Upload.LIST_IGNORE;
                              }

                              return false;
                            }}
                            onChange={({ fileList }) => {
                              setReplyFiles(fileList);
                            }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                            >
                              Upload Files
                            </Button>
                          </Upload>
                        </Form.Item>

                        <Divider />

                        {/* Buttons */}

                        <Row
                          justify="end"
                          gutter={12}
                        >
                          <Col>
                            <Button
                              onClick={() => {
                                replyForm.resetFields();
                                setReplyFiles([]);
                              }}
                            >
                              Cancel
                            </Button>
                          </Col>

                          <Col>
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<SendOutlined />}
                            >
                              Submit Comment
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    ),
                  },
                ]}
              />

              <div
                style={{
                  textAlign: "right",
                  marginTop: 24,
                }}
              >
                <Button
                  size="large"
                  onClick={onClose}
                >
                  Close
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ViewTicketDrawer;